import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  IGetAllOpportunity,
  IOpportunity,
  IOpportunityCount,
  IOpportunityState,
  IOpportunityTypes,
} from "../types/opportunity";

const initialState: IOpportunityState = {
  opportunities: [],
  opportunity: null,
  opportunity_types: [],
  counts: {
    total_opportunity: 0,
    total_inactive_opportunity: 0,
    total_active_opportunity: 0,
  },
  search: [],
  upload: {},
  total: 0,
  limit: 20,
  offset: 0,
  last_page: 1,
  current_page: 1,
  loading: false,
  loadingOpportunityId: null,
  loadingOpportunityTypeId: null,
  updatingOpportunityId: null,
  update: false,
};

const opportunity = createSlice({
  name: "opportunity",
  initialState,
  reducers: {
    setOpportunities: (state, action: PayloadAction<IGetAllOpportunity>) => {
      const { data, limit, offset, total } = action.payload;
      state.opportunities = Array.isArray(data) ? data : [];
      state.total = total;
      state.limit = limit;
      state.offset = offset;
      state.current_page = limit > 0 ? Math.floor(offset / limit) + 1 : 1;
      state.last_page = Math.max(limit > 0 ? Math.ceil(total / limit) : 1, 1);
    },
    setOpportunity: (state, action: PayloadAction<IOpportunity>) => {
      state.opportunity = action.payload;
    },
    createOpportunity: (state, action: PayloadAction<IOpportunity>) => {
      state.opportunities = [action.payload, ...state.opportunities];
      state.total += 1;
    },
    editOpportunity: (state, action: PayloadAction<IOpportunity>) => {
      state.opportunities = state.opportunities.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );
      if (state.opportunity?.id === action.payload.id) {
        state.opportunity = action.payload;
      }
    },
    updateOpportunityStatus: (
      state,
      action: PayloadAction<Pick<IOpportunity, "id" | "status">>,
    ) => {
      state.opportunities = state.opportunities.map((item) =>
        item.id === action.payload.id
          ? { ...item, status: action.payload.status }
          : item,
      );
      if (state.opportunity?.id === action.payload.id) {
        state.opportunity.status = action.payload.status;
      }
    },
    deleteOpportunity: (state, action: PayloadAction<number>) => {
      const exists = state.opportunities.some(
        (item) => item.id === action.payload,
      );
      state.opportunities = state.opportunities.filter(
        (item) => item.id !== action.payload,
      );
      if (exists) state.total = Math.max(state.total - 1, 0);
      if (state.opportunity?.id === action.payload) state.opportunity = null;
    },
    setOpportunityTypes: (
      state,
      action: PayloadAction<IOpportunityTypes[]>,
    ) => {
      state.opportunity_types = action.payload;
    },
    createOpportunityType: (
      state,
      action: PayloadAction<IOpportunityTypes>,
    ) => {
      state.opportunity_types = [action.payload, ...state.opportunity_types];
    },
    deleteType: (state, action: PayloadAction<number>) => {
      state.opportunity_types = state.opportunity_types.filter(
        (type) => type.id !== action.payload,
      );
    },
    setUpload: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.upload = action.payload;
    },
    setSearch: (state, action: PayloadAction<IOpportunity[]>) => {
      state.search = action.payload;
    },
    setOpportunityLoading: (state) => {
      state.loading = true;
    },
    disableOpportunityLoading: (state) => {
      state.loading = false;
    },
    setOpportunityLoadingId: (state, action: PayloadAction<number | null>) => {
      state.loadingOpportunityId = action.payload;
    },
    setOpportunityTypeLoadingId: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.loadingOpportunityTypeId = action.payload;
    },
    setUpdatingOpportunityId: (state, action: PayloadAction<number | null>) => {
      state.updatingOpportunityId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setUpdate: (state, action: PayloadAction<boolean>) => {
      state.update = action.payload;
    },

    setTotalOpportunity: (
      state,
      action: PayloadAction<IOpportunityCount["data"]>,
    ) => {
      state.counts = action.payload;
    },
  },
});

export const {
  setOpportunities,
  setOpportunity,
  setOpportunityTypes,
  setTotalOpportunity,
  createOpportunityType,
  deleteType,
  createOpportunity,
  editOpportunity,
  updateOpportunityStatus,
  setUpload,
  deleteOpportunity,
  setOpportunityLoading,
  setOpportunityLoadingId,
  setOpportunityTypeLoadingId,
  setUpdatingOpportunityId,
  setSearch,
  disableOpportunityLoading,
  setLoading,
  setUpdate,
} = opportunity.actions;

export default opportunity.reducer;
